const STORAGE_PREFIX = "inflow.demoMode.v1";
const ROLE_STORAGE_PREFIX = "inflow.demoRole.v1";
export const INFLOW_DEMO_MODE_CHANGED_EVENT = "inflow:demo-mode-changed";

export const INFLOW_DEMO_ROLES = Object.freeze({
  team_lead: {
    label: "Team lead",
    canonicalRole: "team_leader",
    scopes: [
      "clients:read", "clients:write", "clients:archive", "clients:merge",
      "properties:read", "properties:write", "listings:read", "listings:write",
      "activities:read", "activities:write", "daily5:read", "daily5:write",
      "newsletters:read", "newsletters:write", "checklists:read", "checklists:write",
      "birthday_cards:read", "birthday_cards:write", "communications:read",
      "communications:send", "research:read", "research:write", "imports:read",
      "imports:write", "settings:read", "settings:write", "delegates:read",
      "delegates:invite", "delegates:revoke", "audit:read",
    ],
  },
  solo_agent: {
    label: "Solo agent",
    canonicalRole: "owner",
    scopes: ["*"],
  },
  assistant: {
    label: "Assistant",
    canonicalRole: "unlicensed_assistant",
    scopes: [
      "clients:read", "clients:write", "properties:read", "properties:write",
      "listings:read", "listings:write", "activities:read", "activities:write",
      "daily5:read", "daily5:write", "newsletters:read", "newsletters:write",
      "birthday_cards:read", "birthday_cards:write", "communications:read",
      "research:read", "research:write", "imports:read", "imports:write",
    ],
  },
  team_agent: {
    label: "Team agent",
    canonicalRole: "licensed_agent",
    scopes: [
      "clients:read", "clients:write", "clients:archive", "clients:merge",
      "properties:read", "properties:write", "listings:read", "listings:write",
      "activities:read", "activities:write", "daily5:read", "daily5:write",
      "newsletters:read", "newsletters:write", "birthday_cards:read",
      "birthday_cards:write", "communications:read", "communications:send",
      "research:read", "research:write", "imports:read", "imports:write",
    ],
  },
});

const DEMO_NAME_PAIRS = [
  // Recognizable male/female movie-actor pairings.
  ["Ryan Gosling", "Rachel McAdams"],
  ["Tom Hanks", "Meg Ryan"],
  ["Harrison Ford", "Karen Allen"],
  ["Denzel Washington", "Viola Davis"],
  ["Keanu Reeves", "Sandra Bullock"],
  ["George Clooney", "Julia Roberts"],
  ["Leonardo DiCaprio", "Kate Winslet"],
  ["Richard Gere", "Julia Roberts"],
  ["Adam Sandler", "Drew Barrymore"],
  ["Matthew McConaughey", "Kate Hudson"],
  ["Patrick Swayze", "Jennifer Grey"],
  ["Pierce Brosnan", "Meryl Streep"],
  ["Steve Martin", "Diane Keaton"],
  ["Oscar Isaac", "Jessica Chastain"],
  // Recognizable male/female Disney character couples.
  ["Mickey Mouse", "Minnie Mouse"],
  ["Donald Duck", "Daisy Duck"],
  ["Aladdin Agrabah", "Jasmine Agrabah"],
  ["Eric Seaward", "Ariel Triton"],
  ["Adam Rose", "Belle Beaumont"],
  ["Simba Pride", "Nala Pride"],
  ["Naveen Maldonia", "Tiana Rogers"],
  ["Eugene Fitzherbert", "Rapunzel Corona"],
  ["Kristoff Bjorgman", "Anna Arendelle"],
  ["Henry Charming", "Ella Tremaine"],
];

const DEMO_LAST_NAMES = [...new Set(DEMO_NAME_PAIRS.flatMap((pair) => (
  pair.map((name) => name.split(/\s+/).slice(1).join(" "))
)))];

const STREET_NAMES = [
  "Silver Screen Lane", "Sunset Story Road", "Starlight Avenue",
  "Cinema Court", "Marquee Street", "Spotlight Drive",
  "Picture House Way", "Premiere Place", "Reelwood Crescent",
];

const NAME_KEYS = new Set([
  "name", "fullname", "displayname", "clientname", "contactname",
  "ownername", "realtorname", "agentname", "assignedtoname",
  "createdbyname", "updatedbyname", "recipientname", "spousename",
  "partnername", "buyername", "sellername",
]);
const FIRST_NAME_KEYS = new Set(["firstname", "preferredname"]);
const LAST_NAME_KEYS = new Set(["lastname", "surname", "familyname"]);
const ADDRESS_KEYS = new Set([
  "address", "fulladdress", "formattedaddress", "addressfull",
  "addresssnapshot", "street", "streetaddress", "streetline", "line1",
]);
const PRIVATE_TEXT_KEYS = new Set([
  "notes", "note", "description", "remarks", "publicremarks",
  "privateremarks", "summary", "message", "body", "content",
]);
const CITY_KEYS = new Set(["city", "municipality", "locality"]);
const POSTAL_KEYS = new Set(["postalcode", "postal", "zipcode", "zip"]);
const PRICE_KEY_PATTERN = /(price|commission|amount|revenue|income|expense|cost|value|deposit|fee|gross|net|balance|proceeds|mortgage)/i;
const DATE_KEY_PATTERN = /(date|at$|deadline|closing|completion|possession)/i;

function storageKey(userId) {
  return `${STORAGE_PREFIX}.${String(userId || "anonymous")}`;
}

function roleStorageKey(userId) {
  return `${ROLE_STORAGE_PREFIX}.${String(userId || "anonymous")}`;
}

export function readInflowDemoMode(userId) {
  if (typeof window === "undefined") return false;
  try { return window.localStorage.getItem(storageKey(userId)) === "1"; }
  catch { return false; }
}

export function writeInflowDemoMode(userId, enabled) {
  if (typeof window === "undefined") return false;
  try {
    const changed = readInflowDemoMode(userId) !== Boolean(enabled);
    window.localStorage.setItem(storageKey(userId), enabled ? "1" : "0");
    window.dispatchEvent(new CustomEvent(INFLOW_DEMO_MODE_CHANGED_EVENT, {
      detail: {
        userId: String(userId || "anonymous"),
        enabled: Boolean(enabled),
        role: readInflowDemoRole(userId),
      },
    }));
    return changed;
  } catch { return false; }
}

export function readInflowDemoRole(userId) {
  if (typeof window === "undefined") return "team_lead";
  try {
    const stored = window.localStorage.getItem(roleStorageKey(userId));
    return INFLOW_DEMO_ROLES[stored] ? stored : "team_lead";
  } catch { return "team_lead"; }
}

export function writeInflowDemoRole(userId, role) {
  if (typeof window === "undefined") return false;
  const normalizedRole = INFLOW_DEMO_ROLES[role] ? role : "team_lead";
  try {
    const changed = readInflowDemoRole(userId) !== normalizedRole;
    window.localStorage.setItem(roleStorageKey(userId), normalizedRole);
    window.dispatchEvent(new CustomEvent(INFLOW_DEMO_MODE_CHANGED_EVENT, {
      detail: {
        userId: String(userId || "anonymous"),
        enabled: readInflowDemoMode(userId),
        role: normalizedRole,
      },
    }));
    return changed;
  } catch { return false; }
}

export function getInflowDemoRoleProfile(userId) {
  const key = readInflowDemoRole(userId);
  return { key, ...INFLOW_DEMO_ROLES[key] };
}

function hash(value) {
  let result = 2166136261;
  for (const character of String(value || "demo")) {
    result ^= character.charCodeAt(0);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}

function identityFor(value, path, recordId) {
  return `${recordId || "record"}:${path}:${String(value || "")}`;
}

function actorName(seed, spouse, usedNames) {
  const pair = DEMO_NAME_PAIRS[hash(seed) % DEMO_NAME_PAIRS.length];
  const pairIndex = spouse === true ? 1 : hash(`${seed}:demo-person`) % 2;
  const baseName = pair[pairIndex];
  if (!usedNames) return baseName;

  const parts = baseName.split(/\s+/);
  const firstName = parts.shift();
  const baseLastName = parts.join(" ");
  const orderedLastNames = [
    baseLastName,
    ...DEMO_LAST_NAMES.filter((lastName) => lastName !== baseLastName),
  ];
  const alternateCount = Math.max(1, orderedLastNames.length - 1);
  const lastNameStart = hash(`${seed}:demo-last-name`) % alternateCount;
  for (let offset = 0; offset < orderedLastNames.length; offset += 1) {
    const lastNameIndex = offset === 0
      ? 0
      : 1 + ((lastNameStart + offset - 1) % alternateCount);
    const lastName = orderedLastNames[lastNameIndex];
    const candidate = `${firstName} ${lastName}`;
    const normalized = candidate.toLocaleLowerCase();
    if (usedNames.has(normalized)) continue;
    usedNames.add(normalized);
    return candidate;
  }

  const fallback = `${firstName} ${baseLastName}-${hash(seed).toString(36).slice(0, 4)}`;
  usedNames.add(fallback.toLocaleLowerCase());
  return fallback;
}

function fakeAddress(seed) {
  const value = hash(seed);
  return `${100 + (value % 9800)} ${STREET_NAMES[value % STREET_NAMES.length]}`;
}

function collectPersonReplacements(value, replacements = new Map(), usedNames = new Set()) {
  if (Array.isArray(value)) {
    value.forEach((item) => collectPersonReplacements(item, replacements, usedNames));
    return replacements;
  }
  if (!value || typeof value !== "object" || value instanceof Blob) return replacements;

  const register = (realName, demoName) => {
    const real = String(realName || "").trim();
    const demo = String(demoName || "").trim();
    if (!real || !demo || real.length < 2) return;
    const normalized = real.toLocaleLowerCase();
    if (!replacements.has(normalized)) replacements.set(normalized, { real, demo });
  };

  const demoFor = (realName, spouse) => {
    const real = String(realName || "").trim();
    const existing = replacements.get(real.toLocaleLowerCase());
    if (existing) return existing.demo;
    return actorName(`person:${real.toLocaleLowerCase()}`, spouse, usedNames);
  };

  const fullName = [value.firstName, value.lastName].filter(Boolean).join(" ").trim();
  if (fullName) {
    const isSpouse = /spouse|partner/i.test(String(value.relationship || value.type || ""));
    const demoFullName = demoFor(fullName, isSpouse ? true : undefined);
    const [demoFirstName, ...demoLastParts] = demoFullName.split(" ");
    register(fullName, demoFullName);
    register(value.firstName, demoFirstName);
    register(value.lastName, demoLastParts.join(" "));
  }

  for (const [key, child] of Object.entries(value)) {
    if (typeof child === "string" && NAME_KEYS.has(key.toLowerCase())) {
      const isSpouse = /spouse|partner/i.test(key) || /spouse|partner/i.test(String(value.relationship || value.type || ""));
      const demoFullName = demoFor(child, isSpouse ? true : undefined);
      const realParts = child.trim().split(/\s+/);
      const demoParts = demoFullName.split(/\s+/);
      register(child, demoFullName);
      register(realParts[0], demoParts[0]);
      if (realParts.length > 1) {
        register(realParts.slice(1).join(" "), demoParts.slice(1).join(" "));
      }
    }
    collectPersonReplacements(child, replacements, usedNames);
  }
  return replacements;
}

function preparePersonReplacements(replacements) {
  const ordered = [...replacements.values()].sort((left, right) => right.real.length - left.real.length);
  const byName = new Map(ordered.map(({ real, demo }) => [real.toLocaleLowerCase(), demo]));
  if (!ordered.length) return { byName, pattern: null };
  const alternatives = ordered
    .map(({ real }) => real.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
  return {
    byName,
    pattern: new RegExp(`(^|[^A-Za-z0-9])(${alternatives})(?=$|[^A-Za-z0-9])`, "gi"),
  };
}

function replaceKnownPeople(value, replacements) {
  const source = String(value || "");
  if (!replacements.pattern) return source;
  replacements.pattern.lastIndex = 0;
  return source.replace(replacements.pattern, (_, prefix, realName) => (
    `${prefix}${replacements.byName.get(realName.toLocaleLowerCase()) || realName}`
  ));
}

function alterNumber(value, seed) {
  if (!Number.isFinite(value) || value === 0) return value;
  const factor = 0.82 + ((hash(seed) % 3300) / 10000);
  return Math.round(value * factor);
}

function shiftDate(value, seed) {
  const parsed = new Date(value);
  if (!value || Number.isNaN(parsed.getTime())) return value;
  const days = 17 + (hash(seed) % 103);
  parsed.setUTCDate(parsed.getUTCDate() + days);
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return parsed.toISOString().slice(0, 10);
  return parsed.toISOString();
}

function transformString(value, key, path, recordId, parent, personReplacements) {
  const normalizedKey = key.toLowerCase();
  const seed = identityFor(value, path, recordId);
  const maskedPeople = replaceKnownPeople(value, personReplacements);
  const isSpouse = /spouse|partner/i.test(normalizedKey) || /spouse|partner/i.test(String(parent?.relationship || parent?.type || ""));
  const pairSide = isSpouse ? true : undefined;
  const looksLikeStreetAddress = /^\s*(?:unit\s+\w+[,-]?\s*)?\d+[a-z]?\s+.+\b(?:road|rd|street|st|avenue|ave|drive|dr|lane|ln|court|ct|crescent|cres|way|place|pl|boulevard|blvd|highway|hwy|trail|terrace|terr)\b/i.test(value);
  if (["avatarurl", "profileimageurl", "profilephotourl", "picture"].includes(normalizedKey)) {
    return "/imgs/demo/demo-profile.png";
  }
  if (looksLikeStreetAddress) return fakeAddress(seed);
  if (FIRST_NAME_KEYS.has(normalizedKey) || LAST_NAME_KEYS.has(normalizedKey) || NAME_KEYS.has(normalizedKey)) {
    if (maskedPeople !== value) return maskedPeople;
  }
  if (FIRST_NAME_KEYS.has(normalizedKey)) return actorName(seed, pairSide).split(" ")[0];
  if (LAST_NAME_KEYS.has(normalizedKey)) return actorName(seed, pairSide).split(" ").slice(1).join(" ");
  if (NAME_KEYS.has(normalizedKey)) return actorName(seed, pairSide);
  if (ADDRESS_KEYS.has(normalizedKey)) return fakeAddress(seed);
  if (CITY_KEYS.has(normalizedKey)) return ["Maple Harbour", "Cedar Bay", "Silver Falls", "Westhaven"][hash(seed) % 4];
  if (POSTAL_KEYS.has(normalizedKey)) return `V${hash(seed) % 9}R ${1 + (hash(seed) % 8)}A${hash(`${seed}:postal`) % 9}`;
  if (PRIVATE_TEXT_KEYS.has(normalizedKey)) return "Demo note: presentation-safe details are shown here.";
  if (/email/i.test(normalizedKey)) return `demo.${hash(seed).toString(36)}@example.com`;
  if (/phone|mobile|telephone/i.test(normalizedKey)) return `555-${String(1000000 + (hash(seed) % 9000000)).slice(0, 3)}-${String(1000000 + (hash(seed) % 9000000)).slice(3)}`;
  if (/mls|listingnumber/i.test(normalizedKey)) return `DEMO-${100000 + (hash(seed) % 900000)}`;
  if (DATE_KEY_PATTERN.test(normalizedKey)) return shiftDate(value, seed);
  return maskedPeople;
}

function transform(value, path = "root", inheritedRecordId = "", personReplacements = preparePersonReplacements(new Map())) {
  if (Array.isArray(value)) {
    if (/\.coordinates$/.test(path) && value.length >= 2 && value.every((item) => typeof item === "number")) {
      const seed = hash(`${inheritedRecordId}:${path}`);
      return [-123.35 + ((seed % 5000) / 10000), 49.05 + (((seed >>> 8) % 3200) / 10000), ...value.slice(2)];
    }
    return value.map((item, index) => transform(item, `${path}.${index}`, inheritedRecordId, personReplacements));
  }
  if (!value || typeof value !== "object" || value instanceof Blob) return value;

  const recordId = value._id || value.id || value.clientId || value.propertyId || inheritedRecordId;
  const next = {};
  for (const [key, child] of Object.entries(value)) {
    const childPath = `${path}.${key}`;
    if (typeof child === "string") next[key] = transformString(child, key, childPath, recordId, value, personReplacements);
    else if (typeof child === "number" && PRICE_KEY_PATTERN.test(key)) next[key] = alterNumber(child, identityFor(child, childPath, recordId));
    else next[key] = transform(child, childPath, recordId, personReplacements);
  }

  if (typeof next.firstName === "string" && typeof next.lastName === "string") {
    const originalFullName = `${value.firstName || ""} ${value.lastName || ""}`.trim();
    const isSpouse = /spouse|partner/i.test(String(value.relationship || value.type || ""));
    const fullName = replaceKnownPeople(originalFullName, personReplacements) || actorName(recordId || path, isSpouse ? true : undefined);
    const parts = fullName.split(" ");
    next.firstName = parts.shift();
    next.lastName = parts.join(" ");
    if (typeof next.name === "string") next.name = fullName;
    if (typeof next.fullName === "string") next.fullName = fullName;
  }
  return next;
}

export function maskInflowDemoData(data) {
  return transform(data, "root", "", preparePersonReplacements(collectPersonReplacements(data)));
}

function applyDemoRole(value, { userId, profile, teamResponse = false } = {}) {
  if (!value || typeof value !== "object") return value;
  if (Array.isArray(value)) {
    const mapped = value.map((item) => applyDemoRole(item, { userId, profile, teamResponse }));
    if (teamResponse && profile?.key === "solo_agent") {
      const current = mapped.filter((item) => {
        const id = item?.delegateUserId || item?.userId || item?.memberId || item?._id || item?.id;
        return String(id || "") === String(userId || "") || item?.isCurrentUser === true;
      });
      return current.length ? current : mapped.slice(0, 1);
    }
    return mapped;
  }

  const next = {};
  for (const [key, child] of Object.entries(value)) {
    next[key] = applyDemoRole(child, { userId, profile, teamResponse });
  }
  const id = value.delegateUserId || value.userId || value.memberId || value._id || value.id;
  const isCurrent = String(id || "") === String(userId || "") || value.isCurrentUser === true;
  if (isCurrent && profile) {
    next.role = profile.canonicalRole;
    next.roles = [profile.canonicalRole];
    next.teamRole = profile.canonicalRole;
    next.inflowRole = profile.canonicalRole;
    next.scopes = profile.scopes;
    next.isTeamLead = profile.key === "team_lead";
    next.isTeamLeader = profile.key === "team_lead";
    next.licenseStatus = profile.key === "assistant" ? "unlicensed" : "licensed";
    next.relationship = ["assistant", "team_agent"].includes(profile.key)
      ? "delegate"
      : "owner";
    next.avatarUrl = "/imgs/demo/demo-profile.png";
    next.image = "/imgs/demo/demo-profile.png";
  }
  if (teamResponse && profile?.key === "solo_agent") {
    for (const key of ["members", "delegates", "teamMembers", "teamLeaders"]) {
      if (Array.isArray(next[key])) next[key] = next[key].filter((item) => item?.isCurrentUser === true || String(item?.userId || item?._id || "") === String(userId || ""));
    }
    next.isTeamLead = false;
    next.isTeamLeader = false;
    next.currentUserRole = "owner";
  } else if (teamResponse && profile) {
    next.currentUserRole = profile.canonicalRole;
    next.isTeamLead = profile.key === "team_lead";
    next.isTeamLeader = profile.key === "team_lead";
  }
  return next;
}

function shapeDemoTeamResponse(data, userId, profile) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  if (!["assistant", "team_agent"].includes(profile?.key)) return data;

  const currentId = String(userId || "");
  const owner = data.workspaceOwner || data.owner || {};
  const demoOwner = {
    ...owner,
    _id: "demo-workspace-owner",
    id: "demo-workspace-owner",
    userId: "demo-workspace-owner",
    delegateUserId: undefined,
    memberId: "demo-workspace-owner",
    name: "Tom Hanks",
    fullName: "Tom Hanks",
    firstName: "Tom",
    lastName: "Hanks",
    email: "demo.teamlead@example.com",
    role: "team_leader",
    roles: ["team_leader"],
    teamRole: "team_leader",
    inflowRole: "team_leader",
    relationship: "owner",
    scopes: INFLOW_DEMO_ROLES.team_lead.scopes,
    isCurrentUser: false,
    isTeamLead: true,
    isTeamLeader: true,
    avatarUrl: "/imgs/demo/demo-profile.png",
    image: "/imgs/demo/demo-profile.png",
  };
  const roster = Array.isArray(data.teamMembers) ? [...data.teamMembers] : [];
  const currentIndex = roster.findIndex((member) => {
    const id = member?.delegateUserId || member?.userId || member?.memberId || member?._id || member?.id;
    return String(id || "") === currentId || member?.isCurrentUser === true;
  });
  const currentMember = currentIndex >= 0
    ? roster[currentIndex]
    : {
        _id: currentId,
        id: currentId,
        userId: currentId,
        name: "Demo Presenter",
        firstName: "Demo",
        lastName: "Presenter",
      };
  const resolvedCurrentMember = applyDemoRole(
    { ...currentMember, isCurrentUser: true },
    { userId: currentId, profile, teamResponse: false },
  );
  if (currentIndex >= 0) roster[currentIndex] = resolvedCurrentMember;
  else roster.push(resolvedCurrentMember);
  if (!roster.some((member) => String(member?.userId || member?._id || "") === demoOwner.userId)) {
    roster.unshift(demoOwner);
  }

  return {
    ...data,
    workspaceOwner: demoOwner,
    owner: data.owner ? demoOwner : data.owner,
    teamMembers: roster,
    members: Array.isArray(data.members)
      ? data.members.map((member) => String(member?.userId || member?._id || "") === currentId ? resolvedCurrentMember : member)
      : data.members,
    currentUser: resolvedCurrentMember,
    currentMember: resolvedCurrentMember,
    currentUserRole: profile.canonicalRole,
    isTeamLead: false,
    isTeamLeader: false,
  };
}

export function isVerifiedSyntheticDatasetResponse(result) {
  // Read the actual fetch response, never localStorage, request headers, or data flags.
  const marker = result?.meta?.response?.headers?.get?.('X-Inflow-Synthetic-Dataset');
  return /^fictional-workspace-v1:[a-f0-9]{24}$/.test(String(marker || ''));
}

export function maskInflowDemoResult(result, { requestUrl = "" } = {}) {
  if (isVerifiedSyntheticDatasetResponse(result)) return result;
  if (!result?.data || !readInflowDemoMode(window.localStorage.getItem("userId"))) return result;
  const userId = window.localStorage.getItem("userId");
  const profile = getInflowDemoRoleProfile(userId);
  const masked = maskInflowDemoData(result.data);
  const roleApplied = applyDemoRole(masked, {
    userId,
    profile,
    teamResponse: String(requestUrl).replace(/^\/+/, "") === "team/members",
  });
  const data = String(requestUrl).replace(/^\/+/, "") === "team/members"
    ? shapeDemoTeamResponse(roleApplied, userId, profile)
    : roleApplied;
  return {
    ...result,
    data,
  };
}

export function isInflowDemoModeEnabled() {
  if (typeof window === "undefined") return false;
  return readInflowDemoMode(window.localStorage.getItem("userId"));
}
